import useItemAnsweredState from "@features/session/hooks/useItemAnsweredState";
import { isWeb } from "@memorang/helpers/platform";
import { getSubmittedRawInputValue } from "@memorang/helpers/user-submitted-answer";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { ConversationRenderer } from "@memorang/ui/components/ConversationRenderer";
import Writing from "@memorang/ui/components/StudySession/Writing";
import { useDevice } from "@memorang/ui/useDevice";
import { Caption } from "react-native-paper";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import ItemWrapper from "../../ItemWrapper";

type Props = {
	item: BaseItemSchema;
	response: Extract<ItemResponse, { type: "writing" }>;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
};

const WritingContainer = ({
	item,
	response,
	toggleShowReadMore,
	originalItemType,
}: Props) => {
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const storedResponse = userResponse?.[item.id!];
	const currentUserResponse =
		(typeof storedResponse === "string" ? storedResponse : undefined) ??
		(item.answer?.aiResponse?.answer || undefined) ??
		getSubmittedRawInputValue(item.answer, "text");
	const { isMobile } = useDevice();
	const setUserResponse = useAIFeedbackStore((state) => state.setUserResponse);
	const handleChangeText = (value: string) => {
		setUserResponse(item.id!, value);
	};
	const discussion = item.content?.discussion;

	const messages = discussion?.map((item) => ({
		name: item.student.name,
		avatarUrl: item.student.avatar.url,
		content: item.post,
	}));
	const showConversationView = messages && messages?.length > 0;

	const instruction = item.content?.instruction;
	const hasStem = item.content?.stem;
	const { isAnswered } = useItemAnsweredState(item);

	return (
		<ItemWrapper
			item={item}
			toggleShowReadMore={toggleShowReadMore}
			response={response}
			originalItemType={originalItemType}
			instructions={
				hasStem
					? typeof instruction === "string"
						? instruction
						: instruction?.text
					: undefined
			}
			footer={
				!isWeb && (
					<Writing
						value={currentUserResponse as string}
						onChangeText={handleChangeText}
						key={item.id}
						disabled={isAnswered}
					/>
				)
			}
		>
			<Box
				paddingHorizontal={isWeb && !isMobile ? 0 : 16}
				gap={16}
				paddingBottom={isWeb ? 0 : 100}
			>
				{showConversationView ? (
					<ConversationRenderer messages={messages} />
				) : null}
				{instruction && !hasStem && (
					<Caption>
						{typeof instruction === "string" ? instruction : instruction.text}
					</Caption>
				)}
				{isWeb && (
					<Writing
						value={currentUserResponse as string}
						onChangeText={handleChangeText}
						key={item.id}
						disabled={isAnswered}
					/>
				)}
			</Box>
		</ItemWrapper>
	);
};

export default WritingContainer;
